<template>
  <div ref="inputDropdown" class="flex flex-col gap-1.5">
    <InputLabel :label="label" :size="size" />

    <ComboboxRoot
      :defaultValue="defaultValue"
      :model-value="modelValue"
      v-model:searchTerm="searchTerm"
      class="relative min-w-[160px] max-w-full"
      :multiple="multiple"
      :filter-function="handleFiltering"
      :open="open"
      @update:open="(value) => toggleOpen(value)"
    >
      <ComboboxAnchor :class="cn(rootVariants({ size, multiple }))" as="div">
        <Tags
          v-if="modelValue?.length > 1 && displayResult"
          :placeholder="placeholder"
          :size="size"
          v-model="modelValue"
          :type="type"
          class="grow"
          @click="open = true"
        />
        <InputDropdownItem
          v-else-if="selectedItem && displayResult"
          :type="type"
          :size="size"
          :item="selectedItem"
          class="grow"
          disabled
          :class="cn(variants({ size }))"
          @click="open = true"
        />
        <div v-else class="grow flex items-center gap-2">
          <Icon
            v-if="icon"
            :name="icon"
            :variant="iconVariant"
            :class="cn(iconSizes({ size }), 'fg-quaternary')"
          />
          <ComboboxInput
            :placeholder="placeholder"
            class="focus:outline-none bg-transparent border-none focus:border-none focus:![box-shadow:none] shadow-none border border-primary grow h-full p-0"
            :class="cn(variants({ size }))"
            @click="open = true"
            @focus="open = true"
          />
        </div>
        <button
          v-if="selectedItem && showClear && canClear && displayResult"
          :class="cn(variants({ size }))"
          @click="clear()"
        >
          <Icon name="close" size="xs" />
        </button>
        <ComboboxTrigger
          v-else
          class="inline-flex"
          :class="cn(variants({ size }))"
        >
          <span v-if="searchTerm" @click.stop="clearSearchTerm()">
            {{ t("components.buttons.cancel") }}
          </span>
          <Icon name="chevron-down" size="xs" v-else />
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxContent
        class="max-h-80 z-50 mt-2 text-primary bg-primary border border-secondary rounded-md shadow-lg overflow-hidden"
        :side="side"
        position="popper"
        :align="align"
        style="width: var(--radix-popper-anchor-width)"
      >
        <ComboboxViewport v-if="hideSelected && isAllSelected">
          <ComboboxEmpty class="p-2.5">
            {{ $t("components.inputs.all_items_selected") }}
          </ComboboxEmpty>
        </ComboboxViewport>
        <ComboboxViewport v-else>
          <ComboboxEmpty class="p-2.5" />
          <ComboboxGroup v-for="(group, index) in groups" :key="index">
            <InputDropdownItem
              v-for="(option, index) in group.options"
              :key="index"
              class="group cursor-pointer font-medium text-primary rounded-sm first:pt-1 last:pb-1 px-3.5 mb-1 flex items-center h-[40px] relative select-none data-[disabled]:text-disabled data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-primary_hover data-[highlighted]:text-secondary_hover data-[state=checked]:bg-active"
              :value="option"
              :item="option"
              :type="type"
              :size="size"
              :labelKey="labelKey"
              :hideSelected="hideSelected"
              @select.prevent="(event) => handleUpdateModelValue(event)"
            >
              <span>
                {{ option.label }}
              </span>
              <template #indicator>
                <ComboboxItemIndicator
                  class="absolute ltr:right-2.5 rtl:left-2.5 w-[25px] inline-flex items-center justify-center"
                >
                  <Icon name="checkmark-default" class="fg-brand-primary" />
                </ComboboxItemIndicator>
              </template>
            </InputDropdownItem>
          </ComboboxGroup>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>

    <InputHint :hint="hint" :size="size" />
  </div>
</template>

<script setup>
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItemIndicator,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from "radix-vue";
import { ref, computed } from "vue";
import { includes, remove } from "lodash";
import { cn } from "@/Utils/cn";
import { InputHint } from "@/SharedComponents/InputHint";
import { InputLabel } from "@/SharedComponents/InputLabel";
import { Tags } from "./";
import { Icon } from "@/SharedComponents/Icon";
import { useI18n } from "vue-i18n";
import { useClickOutside } from "@/Composables/useClickOutside";
import {
  sizes,
  rootVariants,
  variants,
  types,
  InputDropdownItem,
  iconSizes,
} from ".";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  groups: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
  },
  hint: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  multiple: {
    type: Boolean,
    required: false,
  },
  type: {
    type: null,
    validator: (val) => types.includes(val),
    required: false,
    default: "default",
  },
  size: {
    type: null,
    validator: (val) => sizes.includes(val),
    required: false,
    default: "md",
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  icon: {
    type: String,
    required: false,
  },
  iconVariant: {
    type: String,
    default: "outline",
  },
  displayResult: {
    type: Boolean,
    default: true,
  },
  hideSelected: {
    type: Boolean,
    default: false,
  },
  defaultValue: {
    type: Array,
    default: () => [],
  },
  labelKey: {
    type: String,
    default: "label",
  },
  side: {
    type: String,
    default: "bottom",
    validator: (val) => ["top", "bottom", "left", "right"].includes(val),
  },
  align: {
    type: String,
    default: "start",
    validator: (val) => ["start", "center", "end"].includes(val),
  },
  canClear: {
    type: Boolean,
    default: true,
  },
  showClear: {
    type: Boolean,
    default: true,
  },
});
const modelValue = ref(props.modelValue);
const open = ref(false);
const searchTerm = ref("");
const inputDropdown = ref(null);
const { t } = useI18n();

const selectedItem = computed(() => {
  if (!props.multiple) {
    return modelValue.value[0];
  } else {
    return modelValue;
  }
});

const toggleOpen = () => {
  open.value = !open.value;
};

const handleFiltering = (items) => {
  if (searchTerm.value === "") {
    return items;
  }

  return items.filter((item) => {
    if (item.label) {
      return item.label.toLowerCase().includes(searchTerm.value.toLowerCase());
    }
    if (item.fullname) {
      return item.fullname
        .toLowerCase()
        .includes(searchTerm.value.toLowerCase());
    }
  });
};

const handleUpdateModelValue = (event) => {
  const option = event.detail.value;

  if (props.multiple) {
    includes(modelValue.value, option)
      ? remove(modelValue.value, (item) => item === option)
      : modelValue.value.push(option);
  } else {
    modelValue.value = [option];
    open.value = false;
  }
  emit("update:modelValue", modelValue.value);
};

const clear = () => {
  modelValue.value = [];
  emit("update:modelValue", modelValue.value);
  clearSearchTerm();
  open.value = false;
};

const clearSearchTerm = () => {
  searchTerm.value = "";
};

const isAllSelected = computed(() => {
  if (!props.multiple) return false;
  const counter = props.groups.reduce((number, el) => {
    return number + el.options.length;
  }, 0);
  return modelValue.value?.length === counter;
});

useClickOutside(inputDropdown, () => {
  open.value = false;
});
</script>
